import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Layout from '../components/Layout';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { usersAPI } from '../services/api';
import { toast } from 'react-toastify';

const documentTypes = [
  { value: 'Cédula', label: 'Cédula' },
  { value: 'Cédula extranjera', label: 'Cédula Extranjera' },
  { value: 'Pasaporte', label: 'Pasaporte' },
  { value: 'NIT', label: 'NIT' },
];

const UserForm = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = !!userId;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm();

  // Fetch user data if editing
  const { data: userData, isLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => usersAPI.getUserById(userId),
    enabled: isEditMode,
  });

  // Store original email for comparison
  const [originalEmail, setOriginalEmail] = React.useState('');

  // Populate form with existing data
  useEffect(() => {
    if (userData?.data) {
      const user = userData.data;
      const userEmail = user.email || '';
      setOriginalEmail(userEmail);
      reset({
        givenName: user.givenName || '',
        surname: user.surname || '',
        email: userEmail,
        businessPhones: user.businessPhones?.[0] || '',
        documentType: user.documentType || '',
        documentNumber: user.documentNumber || '',
        streetAddress: user.streetAddress || '',
        city: user.city || '',
        state: user.state || '',
        postalCode: user.postalCode || '',
        country: user.country || '',
      });
    }
  }, [userData, reset]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => usersAPI.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      toast.success('Usuario creado exitosamente');
      navigate('/users');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error al crear usuario');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data) => {
      // Check if email changed
      const emailChanged = data.email !== originalEmail;

      if (emailChanged) {
        // First, change the email
        await usersAPI.changeUserEmail(userId, data.email);
      }

      // Then update other fields (exclude email only)
      const { email, ...otherFields } = data;

      // Only call updateUser if there are other fields to update
      if (Object.keys(otherFields).length > 0) {
        return await usersAPI.updateUser(userId, otherFields);
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      queryClient.invalidateQueries(['user', userId]);
      toast.success('Usuario actualizado exitosamente');
      navigate(`/users/${userId}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error al actualizar usuario');
    },
  });

  const onSubmit = (data) => {
    // Calculate displayName automatically from givenName and surname
    const displayName = `${data.givenName || ''} ${data.surname || ''}`.trim();
    const dataWithDisplayName = { ...data, displayName };

    if (isEditMode) {
      updateMutation.mutate(dataWithDisplayName);
    } else {
      createMutation.mutate(dataWithDisplayName);
    }
  };

  // Handle address autocomplete selection
  const handlePlaceSelected = (addressData) => {
    setValue('streetAddress', addressData.streetAddress);
    setValue('city', addressData.city);
    setValue('state', addressData.state);
    setValue('postalCode', addressData.postalCode);
    setValue('country', addressData.country);
  };

  if (isEditMode && isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center">
          <Link
            to={isEditMode ? `/users/${userId}` : '/users'}
            className="mr-4 text-gray-400 hover:text-gray-500"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {isEditMode
                ? 'Actualizar información del usuario'
                : 'Agregar un nuevo usuario a Azure B2C'}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Personal Information */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Personal Information
              </h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="givenName"
                    className="block text-sm font-medium text-gray-700"
                  >
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="givenName"
                    {...register('givenName', {
                      required: 'First name is required',
                    })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  {errors.givenName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.givenName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="surname"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="surname"
                    {...register('surname', { required: 'Last name is required' })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  {errors.surname && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.surname.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                  {isEditMode && (
                    <p className="mt-1 text-xs text-gray-500">
                      Email can be changed, but ensure it's not already in use
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="businessPhones"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Mobile Phone
                  </label>
                  <input
                    type="tel"
                    id="businessPhones"
                    {...register('businessPhones')}
                    placeholder="+57 300 1234567"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Document Information */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Document Information
              </h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="documentType"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Document Type
                  </label>
                  <select
                    id="documentType"
                    {...register('documentType')}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Select a type</option>
                    {documentTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="documentNumber"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Document Number
                  </label>
                  <input
                    type="text"
                    id="documentNumber"
                    {...register('documentNumber')}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Address Information
              </h3>
              <div className="grid grid-cols-1 gap-6">
                {/* Google Places Autocomplete */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search Address
                  </label>
                  <AddressAutocomplete
                    onPlaceSelected={handlePlaceSelected}
                    initialValue=""
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Start typing to search for an address. The fields below will be filled automatically.
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="streetAddress"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Street Address
                  </label>
                  <input
                    type="text"
                    id="streetAddress"
                    {...register('streetAddress')}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="city"
                      className="block text-sm font-medium text-gray-700"
                    >
                      City
                    </label>
                    <input
                      type="text"
                      id="city"
                      {...register('city')}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="state"
                      className="block text-sm font-medium text-gray-700"
                    >
                      State / Province
                    </label>
                    <input
                      type="text"
                      id="state"
                      {...register('state')}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="postalCode"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Postal Code
                    </label>
                    <input
                      type="text"
                      id="postalCode"
                      {...register('postalCode')}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="country"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Country
                    </label>
                    <input
                      type="text"
                      id="country"
                      {...register('country')}
                      placeholder="Colombia"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Password (only for create) */}
          {!isEditMode && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Password
                </h3>
                <div className="max-w-md">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Initial Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    id="password"
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters',
                      },
                    })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.password.message}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Password must be at least 8 characters long
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Link
              to={isEditMode ? `/users/${userId}` : '/users'}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {isEditMode ? 'Actualizando...' : 'Creando...'}
                </span>
              ) : isEditMode ? (
                'Actualizar Usuario'
              ) : (
                'Crear Usuario'
              )}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default UserForm;
